import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { AssessmentAttemptInput, AssessmentInput } from './types';

export const assessmentKeys = {
  all: ['assessments'] as const,
  lists: () => [...assessmentKeys.all, 'list'] as const,
  list: (p: api.AssessmentListParams) => [...assessmentKeys.lists(), p] as const,
  attempts: () => [...assessmentKeys.all, 'attempts'] as const,
  attemptsList: (p: api.AttemptListParams) => [...assessmentKeys.attempts(), p] as const,
};

export function useAssessments(params: api.AssessmentListParams = {}) {
  return useQuery({
    queryKey: assessmentKeys.list(params),
    queryFn: () => api.listAssessments(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssessmentInput) => api.createAssessment(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assessmentKeys.lists() });
      toast.success('Assessment created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<AssessmentInput> }) =>
      api.updateAssessment(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: assessmentKeys.lists() }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteAssessment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assessmentKeys.lists() });
      toast.success('Assessment deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useAttempts(params: api.AttemptListParams = {}) {
  return useQuery({
    queryKey: assessmentKeys.attemptsList(params),
    queryFn: () => api.listAttempts(params),
    placeholderData: keepPreviousData,
  });
}

export function useRecordAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssessmentAttemptInput) => api.createAttempt(input),
    onSuccess: (attempt) => {
      qc.invalidateQueries({ queryKey: assessmentKeys.attempts() });
      toast.success(attempt.passed ? 'Attempt recorded — passed' : 'Attempt recorded — did not pass');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}
